// Services Management Component
import React, { useState, useEffect } from "react";
import FeatureGuard from "../common/FeatureGuard";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  MoreVert,
  ContentCut,
  Refresh,
  Schedule,
} from "@mui/icons-material";
import apiService from "../../services/api";
import authService from "../../services/auth";
import { STATUS_OPTIONS } from "../../constants";
import { useCurrency } from "../../contexts/CurrencyContext";
import SearchableDropdown from "../common/SearchableDropdown";

const Services = () => {
  const { currencySymbol, formatPrice: formatCurrencyPrice } = useCurrency();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterSubCategoryId, setFilterSubCategoryId] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterSubcategories, setFilterSubcategories] = useState([]);
  const [filterSubcategoriesLoading, setFilterSubcategoriesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    member_price: "",
    duration: "60",
    category_id: "",
    sub_category_id: "",
    gender: "",
    status: STATUS_OPTIONS.ACTIVE,
    gst_percentage: "",
    price_inclusive_gst: false,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchServices(), fetchCategories(), fetchPermissions()]);
      } catch (err) {
        setError("Error loading data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const isAdminLike = (user) => String(user?.role_name || "").toLowerCase().includes("admin");

  const hasPermission = (module, action = "view") => {
    const user = authService.getUser();
    if (isAdminLike(user)) return true;
    if (!userPermissions) return false;
    return userPermissions?.[module]?.[action] === true;
  };

  const fetchPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.id) return;
      const response = await apiService.get(`/roles/user/${user.id}/permissions`);
      setUserPermissions(response.data || {});
    } catch (e) {
      // safest fallback: no permissions
      setUserPermissions({});
    }
  };

  const fetchServices = async () => {
    try {
      const response = await apiService.get("/tenant-services");
      if (response.success) {
        setServices(response.data.services || []);
      } else {
        setError("Failed to fetch services");
      }
    } catch (err) {
      // If the session is expired/invalid, the API layer will clear storage + redirect to /login.
      // Avoid briefly showing a noisy error message during that redirect.
      if (err?.isAuthError || err?.message === "Session expired") return;
      setError("Error fetching services: " + err.message);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.get("/tenant-services/categories");
      if (response.success) {
        // Backward-compatible parsing:
        // - New API: { data: { categories: [{ id, name }, ...] } }
        // - Old API: { data: ["Cat1", "Cat2"] }
        const data = response.data;
        let list = [];
        if (Array.isArray(data)) {
          list = data.map((name) => ({ id: name, name }));
        } else if (data?.categories) {
          list = (data.categories || []).map((cat) =>
            typeof cat === "string" ? { id: cat, name: cat } : cat
          );
        }
        setCategories(list);
      } else {
        setError("Failed to fetch categories");
      }
    } catch (err) {
      if (err?.isAuthError || err?.message === "Session expired") return;
      setError("Error fetching categories: " + err.message);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    try {
      setSubcategoriesLoading(true);
      const response = await apiService.get("/tenant-services/subcategories", {
        category_id: categoryId,
      });
      if (response.success) {
        setSubcategories(response.data?.subcategories || []);
      } else {
        setSubcategories([]);
      }
    } catch (err) {
      if (err?.isAuthError || err?.message === "Session expired") return;
      setSubcategories([]);
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  const fetchFilterSubcategories = async (categoryId) => {
    if (!categoryId) {
      setFilterSubcategories([]);
      return;
    }
    try {
      setFilterSubcategoriesLoading(true);
      const response = await apiService.get("/tenant-services/subcategories", {
        category_id: categoryId,
      });
      if (response.success) {
        setFilterSubcategories(response.data?.subcategories || []);
      } else {
        setFilterSubcategories([]);
      }
    } catch (err) {
      if (err?.isAuthError || err?.message === "Session expired") return;
      setFilterSubcategories([]);
    } finally {
      setFilterSubcategoriesLoading(false);
    }
  };

  useEffect(() => {
    // Reset subcategory filter when category changes
    setFilterSubCategoryId("");
    if (filterCategoryId) {
      fetchFilterSubcategories(filterCategoryId);
    } else {
      setFilterSubcategories([]);
    }
  }, [filterCategoryId]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const normalizedFilterGender = String(filterGender || "").trim().toLowerCase();
  const normalizedSearch = String(searchTerm || "").trim().toLowerCase();

  const filteredServices = (services || []).filter((service) => {
    const name = String(service.name || "").toLowerCase();
    const categoryText = String(service.category_name || service.category || "").toLowerCase();
    const description = String(service.description || "").toLowerCase();

    const matchesSearch =
      !normalizedSearch ||
      name.includes(normalizedSearch) ||
      categoryText.includes(normalizedSearch) ||
      description.includes(normalizedSearch);

    const serviceCategoryId =
      service.category_id ?? service.categoryId ?? service.categoryID ?? "";
    const matchesCategory =
      !filterCategoryId || String(serviceCategoryId) === String(filterCategoryId);

    const serviceSubCategoryId =
      service.sub_category_id ??
      service.subCategoryId ??
      service.sub_categoryId ??
      "";
    const matchesSubCategory =
      !filterSubCategoryId ||
      String(serviceSubCategoryId) === String(filterSubCategoryId);

    const serviceGender = String(service.gender || "").trim().toLowerCase();
    const matchesGender = !normalizedFilterGender || serviceGender === normalizedFilterGender;

    return matchesSearch && matchesCategory && matchesSubCategory && matchesGender;
  });

  const hasAnyFilter =
    !!normalizedSearch ||
    !!String(filterCategoryId || "").trim() ||
    !!String(filterSubCategoryId || "").trim() ||
    !!normalizedFilterGender;

  const handleOpenDialog = (service = null) => {
    // Permission checks (UI-level). API also enforces this.
    if (service && !hasPermission("services", "edit")) {
      setError("Permission denied");
      return;
    }
    if (!service && !hasPermission("services", "add")) {
      setError("Permission denied");
      return;
    }

    setDialogError(""); // Clear any previous dialog errors
    if (service) {
      setEditingService(service);
      const initialCategoryId =
        service.category_id ?? service.categoryId ?? service.categoryID ?? "";
      const initialSubCategoryId =
        service.sub_category_id ??
        service.subCategoryId ??
        service.sub_categoryId ??
        "";
      setFormData({
        name: service.name,
        description: service.description || "",
        price: service.price,
        member_price: service.member_price ?? "",
        duration: service.duration || "60",
        category_id: initialCategoryId,
        sub_category_id: initialSubCategoryId,
        gender: service.gender || "",
        status: service.status,
        gst_percentage: service.gst_percentage || "",
        price_inclusive_gst: service.price_inclusive_gst || false,
      });
      if (initialCategoryId) {
        fetchSubcategories(initialCategoryId);
      } else {
        setSubcategories([]);
      }
    } else {
      setEditingService(null);
      setFormData({
        name: "",
        description: "",
        price: "",
        member_price: "",
        duration: "60",
        category_id: "",
        sub_category_id: "",
        gender: "",
        status: STATUS_OPTIONS.ACTIVE,
        gst_percentage: "",
        price_inclusive_gst: false,
      });
      setSubcategories([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingService(null);
    setDialogError(""); // Clear dialog errors when closing
    setFormData({
      name: "",
      description: "",
      price: "",
      member_price: "",
      duration: "",
      category_id: "",
      sub_category_id: "",
      gender: "",
      status: STATUS_OPTIONS.ACTIVE,
    });
    setSubcategories([]);
  };

  const handleInputChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleCheckboxChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSubmit = async () => {
    try {
      console.log("Form Data:", formData);
      setDialogError(""); // Clear any previous dialog errors

      // Permission checks (UI-level). API also enforces this.
      if (editingService && !hasPermission("services", "edit")) {
        setDialogError("Permission denied");
        return;
      }
      if (!editingService && !hasPermission("services", "add")) {
        setDialogError("Permission denied");
        return;
      }
      
      // Prepare data for submission - ensure proper data types
      const submitData = {
        ...formData,
        gst_percentage: formData.gst_percentage === "" ? null : formData.gst_percentage,
        price_inclusive_gst: Boolean(formData.price_inclusive_gst)
      };
      
      let response;
      if (editingService) {
        // Check multiple possible ID fields
        const serviceId =
          editingService.id || editingService.service_id || editingService._id;

        if (!serviceId) {
          setDialogError("Service ID is missing for update operation");
          return;
        }

        console.log("Updating Service with ID:", serviceId);
        response = await apiService.put(`/tenant-services/${serviceId}`, submitData);
      } else {
                  response = await apiService.post("/tenant-services", submitData);
      }

      if (response.success) {
        fetchServices();
        fetchCategories();
        handleCloseDialog();
        // Show success message
        const message = editingService
          ? "Service updated successfully!"
          : "Service created successfully!";
        setSuccessMessage(message);
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        const apiErrors = response?.data?.errors;
        if (apiErrors && typeof apiErrors === "object") {
          const details = Object.values(apiErrors)
            .filter(Boolean)
            .join("\n");
          setDialogError(details || response.message || "Failed to save service");
        } else {
          setDialogError(response.message || "Failed to save service");
        }
      }
    } catch (err) {
      setDialogError(err.message);
    }
  };

  const handleMenuOpen = (event, service) => {
    setAnchorEl(event.currentTarget);
    setSelectedService(service);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedService(null);
  };

  const handleDeleteService = async () => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("services", "delete")) {
      setError("Permission denied");
      handleMenuClose();
      return;
    }

    if (selectedService) {
      try {
        const response = await apiService.delete(
          `/tenant-services/${selectedService.id}`
        );
        if (response.success) {
          fetchServices();
          setSuccessMessage("Service deleted successfully!");
          // Auto-hide success message after 3 seconds
          setTimeout(() => setSuccessMessage(""), 3000);
        } else {
          setError("Failed to delete service");
        }
      } catch (err) {
        setError("Error deleting service: " + err.message);
      }
    }
    handleMenuClose();
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  const formatPrice = (price) => {
    return formatCurrencyPrice(price);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading services...</Typography>
      </Box>
    );
  }

  return (
    <FeatureGuard feature="Service Management">
      <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Services
        </Typography>
        <Box display="flex" gap={2}>
          {hasPermission("services", "add") && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()} 
          >
            Add
          </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Box sx={{ flex: "1 1 260px", minWidth: 220 }}>
              <SearchableDropdown
                label="Category"
                value={filterCategoryId}
                onChange={(value) => setFilterCategoryId(value)}
                options={categories.map((cat) => ({
                  id: cat?.id ?? cat?.name ?? cat,
                  name: cat?.name ?? cat?.category ?? cat,
                  description: cat?.description,
                }))}
                placeholder="All categories"
                showDescription={false}
                searchable={true}
                clearValue=""
              />
            </Box>
            <Box sx={{ flex: "1 1 260px", minWidth: 220 }}>
              <SearchableDropdown
                label="Sub Category"
                value={filterSubCategoryId}
                onChange={(value) => setFilterSubCategoryId(value)}
                options={(filterSubcategories || []).map((sc) => ({
                  id: sc?.ssc_id,
                  name: sc?.ssc_name,
                }))}
                placeholder={filterCategoryId ? "All sub categories" : "Select category first"}
                searchable={false}
                showDescription={false}
                disabled={!filterCategoryId || filterSubcategoriesLoading}
                loading={filterSubcategoriesLoading}
                clearValue=""
              />
            </Box>
            <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
              <SearchableDropdown
                label="Gender"
                value={filterGender}
                onChange={(value) => setFilterGender(value)}
                options={[
                  { id: "Men", name: "Men" },
                  { id: "Women", name: "Women" },
                ]}
                placeholder="All genders"
                searchable={false}
                showDescription={false}
                clearValue=""
              />
            </Box>
          </Box>
          <TextField
            fullWidth
            placeholder="Search services by name, category, or description..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Services Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Sub Category</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell style={{ textAlign: "right" }}>Price</TableCell>
              <TableCell style={{ textAlign: "right" }}>Member Price</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredServices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box py={4}>
                    <ContentCut
                      sx={{ fontSize: 64, color: "grey.400", mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      {hasAnyFilter
                        ? "No services found matching your filters"
                        : "No services yet"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {hasAnyFilter
                        ? "Try adjusting your filters or search terms"
                        : "Add your first service to get started"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredServices.map((service) => (
                <TableRow key={service.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {service.name}
                      </Typography>
                      {service.description && (
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        service.category_name || service.category || "Unknown"
                      }
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {service.sub_category_name || service.subcategory_name || service.sub_category ? (
                      <Chip
                        label={
                          service.sub_category_name ||
                          service.subcategory_name ||
                          service.sub_category
                        }
                        color="info"
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {service.gender ? (
                      <Chip
                        label={service.gender}
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      {/* <AttachMoney sx={{ fontSize: 16, mr: 0.5 }} /> */}
                      <Typography variant="subtitle2" fontWeight="bold">
                        {formatPrice(service.price)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <Typography variant="subtitle2" fontWeight="bold">
                        {service.member_price !== null &&
                        service.member_price !== undefined &&
                        String(service.member_price) !== ""
                          ? formatPrice(service.member_price)
                          : "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Schedule
                        sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }}
                      />
                      <Typography variant="body2">
                        {formatDuration(service.duration)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={service.status}
                      color={
                        service.status === STATUS_OPTIONS.ACTIVE
                          ? "success"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {(hasPermission("services", "edit") || hasPermission("services", "delete")) && (
                      <IconButton onClick={(e) => handleMenuOpen(e, service)}>
                        <MoreVert />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {hasPermission("services", "edit") && (
          <MenuItem
            onClick={() => {
              handleOpenDialog(selectedService);
              handleMenuClose();
            }}
          >
            <Edit sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        {hasPermission("services", "delete") && (
          <MenuItem onClick={handleDeleteService} sx={{ color: "error.main" }}>
            <Delete sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingService ? "Edit Service" : "Add New Service"}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setDialogError("")}
            >
              {dialogError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            {/* Row 1: Service Name (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Service Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Box>

            {/* Row 2: Category + Status */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <SearchableDropdown
                label="Category"
                value={formData.category_id}
                onChange={(value) => {
                  setFormData({
                    ...formData,
                    category_id: value,
                    sub_category_id: "",
                  });
                  fetchSubcategories(value);
                }}
                options={categories.map((cat) => ({
                  id: cat?.id ?? cat?.name ?? cat,
                  name: cat?.name ?? cat?.category ?? cat,
                  description: cat?.description,
                }))}
                placeholder="Search categories..."
                showDescription={true}
                required
              />
              <SearchableDropdown
                label="Subcategory"
                value={formData.sub_category_id}
                onChange={(value) => setFormData({ ...formData, sub_category_id: value })}
                options={(subcategories || []).map((sc) => ({
                  id: sc?.ssc_id,
                  name: sc?.ssc_name,
                }))}
                placeholder={
                  formData.category_id ? "Select subcategory..." : "Select category first"
                }
                searchable={false}
                showDescription={false}
                disabled={!formData.category_id || subcategoriesLoading}
                required
              />
              <SearchableDropdown
                label="Gender"
                value={formData.gender}
                onChange={(value) => setFormData({ ...formData, gender: value })}
                options={[
                  { id: "Men", name: "Men" },
                  { id: "Women", name: "Women" },
                ]}
                placeholder="Select gender..."
                showDescription={false}
                searchable={false}
                required
              />
              <SearchableDropdown
                label="Status"
                value={formData.status}
                onChange={(value) => setFormData({ ...formData, status: value })}
                options={[
                  { id: STATUS_OPTIONS.ACTIVE, name: 'Active', description: 'Service is available for booking' },
                  { id: STATUS_OPTIONS.INACTIVE, name: 'Inactive', description: 'Service is not available for booking' }
                ]}
                placeholder="Select status..."
                showDescription={true}
                searchable={false}
                clearValue={STATUS_OPTIONS.ACTIVE}
              />
            </Box>

            {/* Row 3: Price + Member Price + Duration */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                required
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{currencySymbol}</InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Member Price"
                name="member_price"
                type="number"
                value={formData.member_price}
                onChange={handleInputChange}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{currencySymbol}</InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Duration (minutes)"
                name="duration"
                type="number"
                value={formData.duration}
                onChange={handleInputChange}
                required
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">min</InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Row 4: GST Percentage + Price Inclusive GST */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="GST Percentage (Optional)"
                name="gst_percentage"
                type="number"
                value={formData.gst_percentage}
                onChange={handleInputChange}
                placeholder="0.00"
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 200 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    id="price_inclusive_gst"
                    name="price_inclusive_gst"
                    checked={formData.price_inclusive_gst}
                    onChange={handleCheckboxChange}
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="price_inclusive_gst" style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                    Price is inclusive of GST
                  </label>
                </Box>
              </Box>
            </Box>

            {/* Row 4: Description (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe this service..."
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingService ? "Update" : "Add"} Service
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </FeatureGuard>
  );
};

export default Services;
